/**
 * Domain enums for the placement lifecycle. Kept short; the DB is source of truth for exact spelling.
 */
export const APPLICATION_STAGES = [
    'Applied',
    'Under_Review',
    'Shortlisted',
    'Assessment_Pending',
    'Assessment_Submitted',
    'Interview_Scheduled',
    'Interview_Completed',
    'Offered',
    'Accepted',
    'Rejected',
    'Withdrawn',
    'On_Hold',
];
export const KANBAN_STAGES = [
    'applied',
    'shortlisted',
    'test',
    'interview',
    'offered',
    'rejected',
];
export const DRIVE_STATUS = [
    'draft',
    'open',
    'assessing',
    'interviewing',
    'offers',
    'closed',
    'cancelled',
];
export const DRIVE_TIERS = [0, 1, 2];
export const OFFER_STATUS = [
    'draft',
    'uploaded',
    'with_college',
    'ready',
    'held',
    'check_needed',
    'released',
    'opened',
    'accepted',
    'declined',
    'sent_back',
    'expired',
];
/**
 * Statuses where the offer is with the TPO awaiting a sign-off decision — i.e. company
 * uploaded a letter, checks have run, TPO must release or send back. TPO Today's queue
 * and TPO Offers' Waiting segment use this. Earlier code checked `o.status ===
 * 'awaiting_signoff'` — a value that was NEVER in OFFER_STATUS, so both queues rendered
 * empty at all times.
 */
export const OFFER_AWAITING_SIGNOFF_STATUSES = [
    'uploaded', 'with_college', 'ready', 'held',
];
export function isOfferAwaitingSignoff(s) {
    return !!s && OFFER_AWAITING_SIGNOFF_STATUSES.includes(s);
}
export const OPPORTUNITY_TYPES = [
    'Training',
    'Internship',
    'Placement_OnCampus',
    'Placement_OffCampus',
    'R&D_Project',
    'Workshop',
    'Hackathon',
    'Certification',
    'Mentorship',
    'Contract_Position',
];
/**
 * Human-friendly labels for OPPORTUNITY_TYPES, keyed by the canonical enum value. DB
 * shape stays snake-case + PascalCase for indexes; UI renders through this map so a
 * student sees "On-campus placement" not "Placement_OnCampus". Any renderer that used
 * to `.replace(/_/g, ' ')` should switch to `prettyOpportunityType(t)` for consistency.
 */
export const OPPORTUNITY_TYPE_LABELS = {
    Training: 'Training',
    Internship: 'Internship',
    Placement_OnCampus: 'On-campus placement',
    Placement_OffCampus: 'Off-campus placement',
    'R&D_Project': 'R&D project',
    Workshop: 'Workshop',
    Hackathon: 'Hackathon',
    Certification: 'Certification',
    Mentorship: 'Mentorship',
    Contract_Position: 'Contract position',
};
/** Lookup helper: returns the mapped label, or a snake-underscore-stripped fallback for
 *  legacy free-text values so pre-Step-59 rows still render legibly. */
export function prettyOpportunityType(t) {
    if (!t)
        return '';
    if (OPPORTUNITY_TYPES.includes(t)) {
        return OPPORTUNITY_TYPE_LABELS[t];
    }
    return t.replace(/_/g, ' ');
}
export const QUESTION_TYPES = [
    'single',
    'multi',
    'tf',
    'blank',
    'num',
    'match',
    'order',
    'short',
    'long',
    'code',
    'case',
    'voice',
    // Long-form aliases some controllers use interchangeably with the short codes above.
    'single_choice',
    'multi_choice',
    'true_false',
    'numeric',
];
export const QUESTION_DIFFICULTY = ['easy', 'medium', 'hard'];
export const QUESTION_STATUS = ['draft', 'review', 'approved', 'retired'];
export const INTEGRITY_FLAG_TYPES = [
    'answerMatch',
    'faceMismatch',
    'impossibleSpeed',
    'tabSwitch',
    'paste',
    'codeSimilarity',
    'networkTiming',
];
// v1.5 mental model — six tenant kinds.
//   GROUP / UNIVERSITY / COLLEGE / DEPARTMENT — customer tenants (institutions)
//   KOLLEZ_PUBLIC — Kollez.ai's own tenant hosting the public catalog (§10). Sole system
//                   tenant: exempt from Subscription; cannot be suspended.
//   PARTNER — every Partner has its own child tenant (§14). Ordinary tenant with its own
//             Subscription and MoU relationships.
export const TENANT_KINDS = [
    'GROUP',
    'UNIVERSITY',
    'COLLEGE',
    'DEPARTMENT',
    'KOLLEZ_PUBLIC',
    'PARTNER',
];
// System tenants are exempt from Subscription requirement (no self-Subscription paradox) and
// cannot be suspended via the ordinary /platform/tenants flow. Only KOLLEZ_PUBLIC.
export const SYSTEM_TENANT_KINDS = ['KOLLEZ_PUBLIC'];
/**
 * Interview round types. Canonical DB values are PascalCase/underscored so they sort
 * cleanly + read unambiguously in logs; UI labels drop underscores and pick common
 * campus-hiring phrasing. Renderers use `prettyRoundType(t)`.
 */
export const INTERVIEW_ROUND_TYPES = [
    'Technical',
    'HR',
    'Managerial',
    'Group_Discussion',
    'System_Design',
    'Coding',
    'Aptitude',
    'Case_Study',
    'Culture_Fit',
];
export const INTERVIEW_ROUND_TYPE_LABELS = {
    Technical: 'Technical',
    HR: 'HR',
    Managerial: 'Managerial',
    Group_Discussion: 'Group discussion',
    System_Design: 'System design',
    Coding: 'Coding',
    Aptitude: 'Aptitude',
    Case_Study: 'Case study',
    Culture_Fit: 'Culture fit',
};
/** Lookup helper for interview round type labels; falls back to underscore-strip for
 *  legacy free-text values so pre-Step-80 rows still render legibly. */
export function prettyRoundType(t) {
    if (!t)
        return '';
    if (INTERVIEW_ROUND_TYPES.includes(t)) {
        return INTERVIEW_ROUND_TYPE_LABELS[t];
    }
    return t.replace(/_/g, ' ');
}
/**
 * Interview mode values in the DB. Renderers use `prettyInterviewMode(m)` for the label
 * and `isOnlineMode(m)` for the boolean check ("show meeting link, hide room"). The old
 * `mode === 'online'` check in web pages was checking against a lowercase string that was
 * never in this enum — always false, so the online branch was dead code.
 */
export const INTERVIEW_MODES = ['In_Person', 'Video', 'Phone', 'Online_Test'];
export const INTERVIEW_MODE_LABELS = {
    In_Person: 'In person',
    Video: 'Video call',
    Phone: 'Phone call',
    Online_Test: 'Online test',
};
/** Modes that expect a meeting link (video call, online proctored test) rather than a
 *  physical room. Used by Interviews.tsx / InterviewDetail.tsx to swap Room vs Link UI. */
const ONLINE_MODES = new Set(['Video', 'Online_Test']);
export function isOnlineInterviewMode(m) {
    return !!m && ONLINE_MODES.has(m);
}
export function prettyInterviewMode(m) {
    if (!m)
        return '';
    if (INTERVIEW_MODES.includes(m)) {
        return INTERVIEW_MODE_LABELS[m];
    }
    return m.replace(/_/g, ' ');
}
//# sourceMappingURL=stages.js.map